import { message } from 'antd';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';

import {
  getArtifactConfig,
  isAccessDenied,
  isRevisionConflict,
  normalizeArtifacts,
  waitWriteTurn,
} from './utils';

const api = bindServices('projectBuilder');

const emptyArtifacts = () => normalizeArtifacts(null);

const initialState = {
  project: null,
  artifacts: emptyArtifacts(),
  accessControl: null,
  artifactIssues: [],
  revision: null,
  token: null,
  canManage: false,
  collaborators: [],
  loading: false,
  writing: false,
  initError: null,
  conflict: null,
  uncertain: false,
};

const unwrap = (res) => {
  if (!res || typeof res !== 'object') return res;
  if (res.configurations || res.configurationToken || res.id || res.items) return res;
  if (res.data && typeof res.data === 'object') return res.data;
  return res;
};

const collaboratorQuery = {
  select: 'id,projectId,collaboratorId',
  expand: { collaborator: { select: 'id,fullName,email' } },
};

const pendingMutation = (payload) => {
  if (!payload?.effect) return null;
  const rest = { ...payload };
  delete rest.resolve;
  delete rest.reject;
  return { effect: payload.effect, payload: rest };
};

function* generateToken(projectId, mode, call) {
  try {
    const res = yield call(api.apiRoute, {
      route: `project/config/generateToken/${projectId}/${mode}`,
      method: 'post',
    });
    return unwrap(res);
  } catch (error) {
    return { error };
  }
}

function* readComposition(projectId, token, call) {
  const res = yield call(api.apiRoute, {
    route: `project/config/${projectId}`,
    params: { token },
  });
  return unwrap(res);
}

function* loadCollaborators(projectId, call) {
  const first = unwrap(
    yield call(api.getAll, {
      route: `project/config/collaborators/${projectId}`,
      page: 1,
      limit: 50,
      ...collaboratorQuery,
    }),
  );
  let items = Array.isArray(first?.items) ? first.items : [];
  const total = typeof first?.totalCount === 'number' ? first.totalCount : items.length;
  let page = 1;
  while (items.length < total && page < 10) {
    page += 1;
    const next = unwrap(
      yield call(api.getAll, {
        route: `project/config/collaborators/${projectId}`,
        page,
        limit: 50,
        ...collaboratorQuery,
      }),
    );
    const batch = Array.isArray(next?.items) ? next.items : [];
    if (!batch.length) break;
    items = items.concat(batch);
  }
  return items;
}

const Model = {
  namespace: 'projectBuilder',
  state: initialState,

  effects: {
    initBuilder: [
      function* initBuilder({ payload }, { call, put }) {
        const { projectId } = payload || {};
        if (!projectId) return;

        yield put({ type: 'resetBuilder' });
        yield put({ type: 'setLoading', payload: true });

        let project;
        try {
          project = unwrap(yield call(api.getById, { route: 'project', id: projectId }));
          if (!project?.id) throw new Error('not-found');
        } catch (e) {
          yield put({ type: 'setLoading', payload: false });
          yield put({ type: 'initFailed' });
          return;
        }

        yield put({
          type: 'saveProject',
          payload: {
            ...project,
            isActive: project.isActive === true,
          },
        });

        const writeToken = yield* generateToken(projectId, CoreEnums.AccessMode.Write, call);
        let token = writeToken?.token ? writeToken : null;
        let canManage = !!token;

        if (!token) {
          if (writeToken?.error && !isAccessDenied(writeToken.error)) {
            yield put({ type: 'setLoading', payload: false });
            yield put({ type: 'initFailed' });
            return;
          }
          const readToken = yield* generateToken(projectId, CoreEnums.AccessMode.ReadOnly, call);
          if (!readToken?.token) {
            yield put({ type: 'saveToken', payload: { token: null, canManage: false } });
            yield put({ type: 'setLoading', payload: false });
            return;
          }
          token = readToken;
        }

        yield put({ type: 'saveToken', payload: { token, canManage } });

        try {
          const composition = yield* readComposition(projectId, token.token, call);
          yield put({ type: 'saveComposition', payload: composition });
        } catch (e) {
          // Metadata stays visible. Composition stays empty until a later reload.
        }

        if (canManage) {
          try {
            const collaborators = yield* loadCollaborators(projectId, call);
            yield put({ type: 'saveCollaborators', payload: collaborators });
          } catch (e) {
            yield put({ type: 'saveCollaborators', payload: [] });
          }
        }

        yield put({ type: 'setLoading', payload: false });
      },
      { type: 'takeLatest' },
    ],

    *reloadComposition(_, { call, put, select }) {
      const projectId = yield select((s) => s.projectBuilder.project?.id);
      const mode = yield select((s) =>
        s.projectBuilder.canManage ? CoreEnums.AccessMode.Write : CoreEnums.AccessMode.ReadOnly,
      );
      if (!projectId) return;

      const token = yield* generateToken(projectId, mode, call);
      if (!token?.token) {
        if (token?.error && isAccessDenied(token.error) && mode === CoreEnums.AccessMode.Write) {
          const readToken = yield* generateToken(projectId, CoreEnums.AccessMode.ReadOnly, call);
          if (readToken?.token) {
            yield put({ type: 'saveToken', payload: { token: readToken, canManage: false } });
            const composition = yield* readComposition(projectId, readToken.token, call);
            yield put({ type: 'saveComposition', payload: composition });
          }
        }
        return;
      }

      yield put({
        type: 'saveToken',
        payload: { token, canManage: mode === CoreEnums.AccessMode.Write },
      });
      const composition = yield* readComposition(projectId, token.token, call);
      yield put({ type: 'saveComposition', payload: composition });
    },

    *updateMetadata({ payload }, { call, put, select }) {
      const current = yield select((s) => s.projectBuilder.project);
      if (!current?.id) return;
      const body = {
        id: current.id,
        name: payload.name,
        description: payload.description,
        isActive: payload.isActive === true,
      };
      try {
        yield call(api.update, { route: 'project', data: body });
        yield put({ type: 'saveProject', payload: { ...current, ...body } });
        yield put({
          type: 'launchpad/syncItemActive',
          payload: { type: 'projects', id: current.id, isActive: body.isActive },
        });
        payload.resolve?.(body);
      } catch (error) {
        payload.reject?.(error);
      }
    },

    *setActive({ payload }, { call, put, select }) {
      const { id, isActive } = payload || {};
      if (!id) return;
      try {
        yield call(api.activate, { route: 'project', id, isActive: isActive === true });
        const current = yield select((s) => s.projectBuilder.project);
        if (current?.id === id) {
          yield put({ type: 'saveProject', payload: { ...current, isActive: isActive === true } });
        }
        yield put({
          type: 'launchpad/syncItemActive',
          payload: { type: 'projects', id, isActive: isActive === true },
        });
        payload.resolve?.();
      } catch (error) {
        payload.reject?.(error);
      }
    },

    *deleteProject({ payload }, { call, put }) {
      const { id } = payload || {};
      if (!id) return;
      try {
        yield call(api.remove, { route: 'project', id });
        yield put({ type: 'launchpad/homeRemoveItem', payload: { type: 'projects', id } });
        payload.resolve?.();
      } catch (error) {
        payload.reject?.(error);
      }
    },

    *createProject({ payload }, { call }) {
      try {
        const created = unwrap(
          yield call(api.create, {
            route: 'project',
            data: {
              name: payload.name,
              description: payload.description,
              isActive: payload.isActive === true,
            },
          }),
        );
        if (!created?.id) throw new Error('missing-id');
        payload.resolve?.(created);
      } catch (error) {
        payload.reject?.(error);
      }
    },

    *appendArtifact({ payload }, { call, put, select }) {
      const config = getArtifactConfig(payload.elementType);
      const artifacts = yield select((state) => state.projectBuilder.artifacts);
      const rows = artifacts?.[config?.collection] || [];
      const already = rows.some(
        (row) =>
          String(row?.id || '').toLowerCase() === String(payload.artifactId || '').toLowerCase(),
      );
      if (already) {
        payload.resolve?.();
        return;
      }
      const next = { ...payload, effect: 'projectBuilder/appendArtifact' };
      yield* runCompositionWrite(
        {
          elementType: next.elementType,
          data: { id: next.artifactId },
        },
        'append',
        { call, put, select, payload: next },
      );
    },

    *refreshArtifact({ payload }, { call, put, select }) {
      const next = { ...payload, effect: 'projectBuilder/refreshArtifact' };
      yield* runCompositionWrite(
        {
          elementType: next.elementType,
          itemId: next.artifactId,
          data: {},
        },
        'modify',
        { call, put, select, payload: next },
      );
    },

    *removeArtifact({ payload }, { call, put, select }) {
      const next = { ...payload, effect: 'projectBuilder/removeArtifact' };
      yield* runCompositionWrite(
        {
          elementType: next.elementType,
          itemId: next.artifactId,
        },
        'remove',
        { call, put, select, payload: next },
      );
    },

    *saveAccessControl({ payload }, { call, put, select }) {
      const data = payload?.data || {};
      const hasRules = ['users', 'positions', 'areas', 'userGroups'].some(
        (key) => Array.isArray(data[key]) && data[key].length > 0,
      );
      const current = yield select((s) => s.projectBuilder.accessControl);
      const exists = !!current;

      if (!hasRules && !exists) {
        payload.resolve?.();
        return;
      }

      const route = !hasRules ? 'remove' : exists ? 'modify' : 'append';
      const body = !hasRules
        ? { elementType: 'access-control' }
        : { elementType: 'access-control', data };
      yield* runCompositionWrite(body, route, {
        call,
        put,
        select,
        payload: { ...payload, effect: 'projectBuilder/saveAccessControl' },
      });
    },

    *addCollaborator({ payload }, { call, put, select }) {
      const projectId = yield select((s) => s.projectBuilder.project?.id);
      if (!projectId || !payload?.collaboratorId) return;
      try {
        yield call(api.apiRoute, {
          route: `project/config/collaborators/${projectId}`,
          method: 'post',
          data: { collaboratorId: payload.collaboratorId },
        });
        const collaborators = yield* loadCollaborators(projectId, call);
        yield put({ type: 'saveCollaborators', payload: collaborators });
        payload.resolve?.();
      } catch (error) {
        payload.reject?.(error);
      }
    },

    *removeCollaborator({ payload }, { call, put, select }) {
      const projectId = yield select((s) => s.projectBuilder.project?.id);
      if (!projectId || !payload?.id) return;
      try {
        yield call(api.remove, {
          route: `project/config/collaborators/${projectId}`,
          id: payload.id,
        });
        const collaborators = yield* loadCollaborators(projectId, call);
        yield put({ type: 'saveCollaborators', payload: collaborators });
        payload.resolve?.();
      } catch (error) {
        payload.reject?.(error);
      }
    },

    *dismissConflict(_, { put }) {
      yield put({ type: 'clearReview' });
    },

    *retryConflict({ payload }, { put }) {
      const pending = payload?.pending;
      yield put({ type: 'clearReview' });
      if (!pending?.effect) return;
      yield put({ type: pending.effect, payload: pending.payload });
    },
  },

  reducers: {
    resetBuilder() {
      return { ...initialState, artifacts: emptyArtifacts() };
    },
    setLoading(state, { payload }) {
      return { ...state, loading: payload === true };
    },
    initFailed(state) {
      return { ...state, initError: 'project', loading: false };
    },
    saveProject(state, { payload }) {
      return { ...state, project: payload };
    },
    saveToken(state, { payload }) {
      return {
        ...state,
        token: payload?.token || null,
        canManage: payload?.canManage === true,
        revision: payload?.token?.revision ?? state.revision,
      };
    },
    saveComposition(state, { payload }) {
      const composition = payload || {};
      const configurations = composition.configurations || {};
      return {
        ...state,
        artifacts: normalizeArtifacts(configurations.artifacts),
        accessControl: configurations.accessControl || null,
        artifactIssues: Array.isArray(composition.artifactIssues) ? composition.artifactIssues : [],
        revision: composition.revision ?? state.revision,
        project: state.project
          ? {
              ...state.project,
              name: composition.name || state.project.name,
              revision: composition.revision ?? state.project.revision,
            }
          : state.project,
      };
    },
    saveCollaborators(state, { payload }) {
      return { ...state, collaborators: Array.isArray(payload) ? payload : [] };
    },
    setDocumentFolderId(state, { payload }) {
      if (!state.project || !payload) return state;
      return { ...state, project: { ...state.project, documentFolderId: payload } };
    },
    setWriting(state, { payload }) {
      return { ...state, writing: payload === true };
    },
    setConflict(state, { payload }) {
      return { ...state, conflict: payload, uncertain: false, writing: false };
    },
    setUncertain(state, { payload }) {
      return { ...state, uncertain: true, conflict: payload || state.conflict, writing: false };
    },
    clearReview(state) {
      return { ...state, conflict: null, uncertain: false };
    },
  },
};

function* runCompositionWrite(body, action, { call, put, select, payload }) {
  const release = yield call(waitWriteTurn);
  try {
    const canManage = yield select((s) => s.projectBuilder.canManage);
    const token = yield select((s) => s.projectBuilder.token);
    const projectId = yield select((s) => s.projectBuilder.project?.id);
    if (!canManage || !token?.token || !projectId) {
      payload.reject?.(new Error('read-only'));
      return;
    }

    yield put({ type: 'setWriting', payload: true });
    let result;
    try {
      result = unwrap(
        yield call(api.apiRoute, {
          route: `project/config/${action}/${projectId}`,
          method: 'post',
          params: { token: token.token },
          data: body,
        }),
      );
    } catch (error) {
      yield put({ type: 'setWriting', payload: false });
      const pending = pendingMutation(payload);
      if (isRevisionConflict(error)) {
        yield put({ type: 'setConflict', payload: pending });
        yield put({ type: 'reloadComposition' });
        payload.reject?.(error);
        return;
      }
      const httpStatus = error?.response?.status;
      const handled = error?.validationErrors || isAccessDenied(error);
      if (!handled && (httpStatus == null || httpStatus >= 500)) {
        yield put({ type: 'setUncertain', payload: pending });
        yield put({ type: 'reloadComposition' });
      }
      payload.reject?.(error);
      return;
    }

    const nextToken = result?.configurationToken;
    if (!nextToken?.token) {
      const pending = pendingMutation(payload);
      yield put({ type: 'setUncertain', payload: pending });
      yield put({ type: 'reloadComposition' });
      payload.reject?.(new Error('missing-token'));
      return;
    }

    yield put({ type: 'saveToken', payload: { token: nextToken, canManage: true } });
    try {
      const composition = yield* readComposition(projectId, nextToken.token, call);
      yield put({ type: 'saveComposition', payload: composition });
    } catch (e) {
      message.warning(getIntl().formatMessage({ id: 'projectBuilder.review.uncertain' }));
    }
    yield put({ type: 'setWriting', payload: false });
    payload.resolve?.(result);
  } finally {
    release();
  }
}

export function ensureProjectBuilderModel() {
  const app = typeof window !== 'undefined' && window.g_app;
  if (app && !app._store.getState().projectBuilder) {
    app.model(Model);
  }
}

export default Model;
